import { collection, doc, getDocs, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../hooks/useAuth";
import { db } from "../network/firebaseConfig";
import CustomSelect from "../components/common/CustomSelect";
import { Loading } from "../components/common/Loading";
import ComplaintForm from "../components/ComplaintForm"
import dayjs from "dayjs";

const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);

const ComplaintList = () => {
    const { user, loading: isLoading, users, usersLoading } = useAuth();
    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(true);

    // Status options
    const statusOptions = ["in-operation", "completed", "paused"];

    const [isFormVisible, setIsFormVisible] = useState(null); // 👈 new
    // const [technicians, setTechnicians] = useState([]);

    const [showCommentModal, setShowCommentModal] = useState(false);
    const [selectedComplaint, setSelectedComplaint] = useState(null);
    const [completionComment, setCompletionComment] = useState("");

    const technicians = users.filter((u) => u.role == "technician");

    // Load complaints with real-time updates
    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "complaints"),
            (snapshot) => {
                let allComplaints = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Filter out completed/paused complaints for this page
                allComplaints = allComplaints.filter(
                    (c) => c.status !== "completed" && c.status !== "paused"
                );

                // Role-based filtering
                if (user?.role === "technician") {
                    allComplaints = allComplaints.filter(
                        (c) => c.assignedTo === user.uid
                    );
                } else if (user?.role === "helpdesk") {
                    allComplaints = allComplaints.filter(
                        (c) => c.createdBy === user.uid
                    );
                } // admin sees all

                // Reverse order: newest first
                allComplaints.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);

                setComplaints(allComplaints);
                setLoading(false);
            },
            (err) => {
                console.error(err);
                toast.error("Failed to load complaints");
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user]);


    // Assign technician
    const handleAssignTechnician = async (complaintId, technicianId) => {
        if (user.role !== "admin" && user.role !== "helpdesk" && user.role !== "supervisor") {
            return toast.error("You do not have permission for this action");
        }
        try {
            const complaintRef = doc(db, "complaints", complaintId);
            const complaint = complaints.find((c) => c.id === complaintId);
            await updateDoc(complaintRef, {
                assignedTo: technicianId,
                status: "in-operation",
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: new Date(),
                        message: `Assigned to technician (ID: ${technicianId})`,
                        userId: user.uid,
                    },
                ],
            });
            toast.success("Technician assigned & status updated!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to assign technician");
        }
    };

    // Change status
    const handleStatusChange = (complaintId, newStatus) => {
        if (
            user.role !== "admin" &&
            user.role !== "helpdesk" &&
            user.role !== "supervisor"
        ) {
            return toast.error("You do not have permission for this action");
        }

        const complaint = complaints.find((c) => c.id === complaintId);

        if (newStatus === "completed") {
            setSelectedComplaint(complaint);
            setShowCommentModal(true);
            return; // 🛑 stop here; we’ll update after comment submission
        }

        // Normal update for other statuses
        updateComplaintStatus(complaint, newStatus);
    };


    const updateComplaintStatus = async (complaint, newStatus, comment = "") => {
        try {
            const complaintRef = doc(db, "complaints", complaint.id);
            await updateDoc(complaintRef, {
                status: newStatus,
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: new Date(),
                        message:
                            newStatus === "completed"
                                ? `Status changed to completed. Comment: ${comment}`
                                : `Status changed to ${newStatus}`,
                        userId: user.uid,
                    },
                ],
                ...(newStatus === "completed" && { completionComment: comment, resolvedAt: new Date() }), // optional: store in root
            });
            toast.success("Status updated!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status");
        }
    };


    const handleTimeChange = async (complaintId, newTime) => {
        if (user.role !== "admin" && user.role !== "helpdesk" && user.role !== "supervisor") {
            return toast.error("You do not have permission for this action");
        }
        try {
            const complaintRef = doc(db, "complaints", complaintId);
            const complaint = complaints.find((c) => c.id === complaintId);
            await updateDoc(complaintRef, {
                approximateResolveTime: newTime,
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: new Date(),
                        message: `approximateResolveTime changed to ${newTime}`,
                        userId: user.uid,
                    },
                ],
            });
            toast.success("approximateResolveTime updated!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update approximateResolveTime");
        }
    }

    if (loading || isLoading) return <Loading />;
    if (!complaints.length) return <p>No active complaints found.</p>;

    return (
        <>

            {isFormVisible && (
                <ComplaintForm
                    onClose={() => setIsFormVisible(false)}
                />
            )}
            {!isFormVisible && (
                <div className="max-w-6xl mx-auto p-0 space-y-6">
                    <div className="flex flex-row justify-between">
                        <h2 className="text-xl font-semibold">Active Complaints</h2>
                        <button className="bg-pink-400 text-gray-100 py-1 px-2 rounded-md"
                            onClick={() => {
                                if (user && (user.role === "admin" || user.role === "helpdesk" || user.role === "supervisor")) {
                                    setIsFormVisible(true)
                                } else {
                                    return toast.error("You do not have permission for this action");
                                }
                            }}
                        >+ Record Complaint</button>
                    </div>
                    <table className="w-full table-auto border-collapse border border-gray-300">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border p-1 text-center">C.. Date</th>
                                <th className="border p-1 text-center">Ticket</th>
                                <th className="border p-1 text-center">Customer</th>
                                <th className="border p-1 text-center">Phone</th>
                                <th className="border p-1 text-center">Departmt</th>
                                <th className="border p-1 text-center">ONU Status</th>
                                <th className="border p-1 text-center">Router Status</th>
                                <th className="border p-1 text-center">Approx.Time</th>
                                <th className="border p-1 text-center">Assigned To</th>
                                <th className="border p-1 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {complaints.map((c) => (
                                <tr key={c.id} className="hover:bg-gray-50">
                                    <td className="border p-1 text-center w-20">{dayjs(c.createdAt.toDate()).format("DD-MM-YY")}</td>
                                    <td className="border p-1 text-center w-24">{c.ticketNumber}</td>
                                    <td className="border p-1 text-center">{
                                        c.customerInfo?.name.length > 8 ?
                                            `${c.customerInfo?.name.slice(0, 7)}...` : c.customerInfo?.name}</td>
                                    <td className="border p-1 text-center">{c.customerInfo?.phone}</td>
                                    <td className="border p-1 text-center">{c.department}</td>

                                    <td className="border p-1">
                                        <div className="flex flex-col">
                                            <strong>Power: {c.onu?.power}</strong>
                                            <strong>PON: {c.onu?.pon}</strong>
                                            <strong>LAN: {c.onu?.lan}</strong>
                                        </div>
                                    </td>
                                    <td className="border p-1">
                                        <div className="flex flex-col">
                                            <strong>Power: {c.router?.power}</strong>
                                            <strong>WLAN: {c.router?.wanLan}</strong>
                                            <strong>WIFI: {c.router?.wifi}</strong>
                                        </div>
                                    </td>
                                    <td className="border p-1 text-center">
                                        <CustomSelect
                                            name="approximateResolveTime"
                                            placeholder="Select Time"
                                            options={resolveTimeOptions}
                                            value={c.approximateResolveTime}
                                            onChange={(name, value) => handleTimeChange(c.id, value)}
                                        />
                                    </td>
                                    <td className="border p-1 w-40">
                                        <select
                                            value={c.assignedTo || ""}
                                            onChange={(e) => handleAssignTechnician(c.id, e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1 focus:ring-1 focus:ring-gray-400 focus:outline-none"
                                        >
                                            <option value="">Assign Technician</option>
                                            {technicians.map((tech) => (
                                                <option key={tech.uid} value={tech.uid}>
                                                    {tech.name} ({tech.department})
                                                </option>
                                            ))}
                                        </select>
                                    </td>


                                    <td className="border p-1 w-32">
                                        <CustomSelect
                                            name="status"
                                            placeholder="Status"
                                            options={statusOptions}
                                            value={c.status}
                                            onChange={(name, value) => handleStatusChange(c.id, value)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {showCommentModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                            <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                                <h3 className="text-lg font-semibold mb-4 text-center">
                                    Add Completion Comment
                                </h3>
                                <textarea
                                    value={completionComment}
                                    onChange={(e) => setCompletionComment(e.target.value)}
                                    placeholder="Enter a comment about how the task was completed..."
                                    className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring focus:ring-gray-300"
                                    rows={4}
                                />
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => {
                                            setShowCommentModal(false);
                                            setCompletionComment("");
                                        }}
                                        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (!completionComment.trim()) {
                                                toast.error("Please add a comment before completing.");
                                                return;
                                            }
                                            updateComplaintStatus(selectedComplaint, "completed", completionComment);
                                            setShowCommentModal(false);
                                            setCompletionComment("");
                                            setSelectedComplaint(null);
                                        }}
                                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            )}
        </>
    );
};

export default ComplaintList;
